import { api } from 'api';

import { AppThunk } from 'store';
import { setIsLoading as setCardLoader } from 'store/reducers/cardsSlice';
import {
  setError,
  setGiftCards,
  setIsLoading,
  setIsRequiredFormsLoading,
  setIsServiceHistoryLoading,
  setIsSignedFormsLoading,
  setRecentServices,
  setReferenceData,
  setRequiredForms,
  setSignedForms,
  setUpcomingServices
} from 'store/reducers/clientSlice';

import {
  GiftCardInterface,
  RefCodeSubmitType,
  ServiceHistoryInterface
} from 'types';

export const getRecentServices =
  (number: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.client.getPastServices(number);
      dispatch(setRecentServices(data));
    } catch (error) {
      console.error(`Error fetching recent services: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const getRecentServiceHistory =
  (
    number: number,
    serviceId: number
  ): AppThunk<Promise<ServiceHistoryInterface[]>> =>
  async (dispatch) => {
    try {
      dispatch(setIsServiceHistoryLoading(true));
      const { data } = await api.client.getRecentServices({
        items: number,
        svc_id: serviceId
      });
      return data || [];
    } catch (error) {
      console.error(`Error fetching recent service history: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return [];
    } finally {
      dispatch(setIsServiceHistoryLoading(false));
    }
  };

export const getUpcomingServices = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    const { data } = await api.client.getUpcomingServices();
    dispatch(setUpcomingServices(data));
  } catch (error) {
    console.error(`Error fetching upcoming services: ${error.message}`);
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const getGiftCards = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsLoading(true));
    const { data } = await api.client.getClGiftCards();
    const gCardList = data.filter((card: GiftCardInterface) =>
      Boolean(card.amt_balance)
    );

    dispatch(setGiftCards(gCardList));
  } catch (error) {
    console.error(`Error while get gift cards: ${error.message}`);
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const getClientRequiredForms = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsRequiredFormsLoading(true));
    const { data } = await api.client.getClRequiredForms();
    dispatch(setRequiredForms(data));
  } catch (error) {
    console.error(`Error while get required forms: ${error.message}`);
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setIsRequiredFormsLoading(false));
  }
};

export const getClientPastForms = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setIsSignedFormsLoading(true));
    const { data } = await api.client.getClPastForms();
    dispatch(setSignedForms(data));
  } catch (error) {
    console.error(`Error while get past forms: ${error.message}`);
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setIsSignedFormsLoading(false));
  }
};

export const addClGiftCard =
  (serial: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(setCardLoader(true));
      const { data } = await api.client.addClGiftCard(serial);

      if (data?.success) {
        const { data: listData } = await api.client.getClGiftCards();
        const cardsList = listData.filter((card: GiftCardInterface) =>
          Boolean(card.amt_balance)
        );

        dispatch(setGiftCards(cardsList));
      }

      return data?.success;
    } catch (error) {
      console.error(
        `Error while add new gift card: ${
          error.response?.data?.error ?? error.message
        }`
      );
      dispatch(setError(error.response?.data?.error || error.message));
    } finally {
      dispatch(setCardLoader(false));
    }
  };

export const getReferralPrefs = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setCardLoader(true));
    const { data } = await api.client.getReferralPrefs();

    dispatch(setReferenceData(data));
  } catch (error) {
    console.error(
      `Error while get referral preferences/details: ${
        error.response?.data?.error ?? error.message
      }`
    );
    dispatch(setError(error.response?.data?.error || error.message));
  } finally {
    dispatch(setCardLoader(false));
  }
};

export const submitReferral =
  (friendsList: RefCodeSubmitType[], onSuccess: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(setCardLoader(true));

    const promises = friendsList.map(async (friend) => {
      try {
        const { data } = await api.client.submitReferral(friend);
        if (data?.success) {
          return data?.success;
        }
        // Reject with an Error
        throw new Error(
          `${friend.email ?? friend.phone} not valid for user ${friend.givname}`
        );
      } catch (error) {
        throw new Error(error.message || 'An error occurred during referral');
      }
    });

    Promise.all(promises)
      .then(onSuccess)
      .catch((error) => dispatch(setError(error)))
      .finally(() => setCardLoader(false));
  };
